import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import { GaugeChart as Gauge, GaugeSeriesOption } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { ComposeOption } from 'echarts/core';

echarts.use([Gauge, CanvasRenderer]);

type ECOption = ComposeOption<GaugeSeriesOption>;

type GaugeChartProps = {
  type: string;
  score: number;
  levels: {
    [score: number]: {
      title: string;
      color: string;
    };
  };
};

const GaugeChart: React.FC<GaugeChartProps> = ({ type, score, levels }) => {
  const chartRef = useRef<HTMLDivElement | null>(null);

  const minimum: number = type === 'anxiety' ? 20 : 0;

  const levelKeys: number[] = Object.keys(levels).map(Number);
  const maximum: number = Math.max(...levelKeys);

  useEffect(() => {
    if (!chartRef.current) return;

    const myChart = echarts.init(chartRef.current);

    // Преобразуем объект levels в массив цветовых зон для графика
    const sortedLevels = levelKeys.sort((a, b) => a - b);
    const colorStops: Array<[number, string]> = [];

    sortedLevels.forEach(level => {
      const normalizedValue = (level - minimum) / (maximum - minimum);
      colorStops.push([normalizedValue, levels[level].color]);
    });

    const option: ECOption = {
      series: [
        {
          type: 'gauge',
          startAngle: 180,
          endAngle: 0,
          radius: '100%',
          min: minimum,
          max: maximum,
          axisLabel: { show: false },
          axisLine: {
            lineStyle: {
              width: 40,
              color: colorStops,
            },
          },
          pointer: {
            icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z',
            length: '12%',
            width: 20,
            offsetCenter: [0, '-50%'],
            itemStyle: {
              color: 'auto',
            },
          },
          axisTick: {
            lineStyle: {
              width: 0,
            },
          },
          splitLine: {
            lineStyle: {
              width: 0,
            },
          },
          detail: {
            fontSize: 50,
            offsetCenter: [0, '-15%'],
            valueAnimation: true,
            formatter: function (value: number) {
              return Math.round(value) + '';
            },
            color: 'inherit',
          },
          data: [
            {
              value: score,
            },
          ],
        },
      ],
      silent: true,
    };

    myChart.setOption(option);

    const resizeHandler = () => {
      myChart.resize();
    };
    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
      myChart.dispose(); // Чистим ресурсы при размонтировании
    };
  }, [type, score, levels, minimum, maximum, levelKeys]);

  return (
    <div className="h-[150px] overflow-hidden">
      <div ref={chartRef} style={{ height: '300px' }} />
    </div>
  );
};

export default GaugeChart;
